import React, { useState } from 'react';
import { createMapsUrl } from '../location/locationUtils';

// Import RequestDetailsModal from RequestsPage
function RequestDetailsModal({ open, onClose, request, student, isArabic, onAction }) {
  if (!open || !request) return null;

  const copyMeetingLink = () => {
    if (request.meeting_link) {
      navigator.clipboard.writeText(request.meeting_link);
      alert(isArabic ? 'تم نسخ الرابط' : 'Meeting link copied!');
    }
  };

  const getStatusConfig = (status, paymentStatus) => {
    // If accepted but payment pending, show as "Awaiting Payment"
    if (status === 'accepted' && paymentStatus === 'awaiting_payment') {
      return {
        color: '#f59e0b',
        bg: '#fffbeb',
        icon: '💳',
        label: isArabic ? 'في انتظار الدفع' : 'Awaiting Payment'
      };
    }

    // If accepted and paid, show as "Confirmed"
    if (status === 'accepted' && paymentStatus === 'paid') {
      return {
        color: '#16a34a',
        bg: '#f0fdf4',
        icon: '✅',
        label: isArabic ? 'مؤكد' : 'Confirmed'
      };
    }

    // Default statuses
    const configs = {
      pending: { color: '#d97706', bg: '#fff7ed', icon: '⏳', label: isArabic ? 'قيد الانتظار' : 'Pending' },
      declined: { color: '#dc2626', bg: '#fef2f2', icon: '❌', label: isArabic ? 'مرفوض' : 'Declined' },
      rejected: { color: '#dc2626', bg: '#fef2f2', icon: '❌', label: isArabic ? 'مرفوض' : 'Rejected' }
    };
    return configs[status] || configs.pending;
  };

  const statusConfig = getStatusConfig(request.status, request.payment_status);

  // Calculate duration
  const parseTime = (timeStr) => {
    const match = timeStr?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return { hours, minutes };
  };

  const startTime = parseTime(request.time_from);
  const endTime = parseTime(request.time_to);
  let duration = null;
  if (startTime && endTime) {
    const startMinutes = startTime.hours * 60 + startTime.minutes;
    const endMinutes = endTime.hours * 60 + endTime.minutes;
    const diffMinutes = endMinutes - startMinutes;
    duration = diffMinutes > 0 ? `${diffMinutes} ${isArabic ? 'دقيقة' : 'minutes'}` : null;
  }

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl p-6 max-w-2xl w-full relative max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
        >
          ✕
        </button>

        {/* Header */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-gray-900">{isArabic ? 'تفاصيل الطلب' : 'Request Details'}</h2>
            <div
              className="px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2"
              style={{ background: statusConfig.bg, color: statusConfig.color }}
            >
              <span>{statusConfig.icon}</span>
              <span>{statusConfig.label}</span>
            </div>
          </div>
        </div>

        {/* Student Info */}
        {student && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-4">
              <img
                src={student.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
                alt={student.full_name}
                className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
              />
              <div>
                <div className="font-bold text-lg text-gray-900">{student.full_name}</div>
                {student.school || student.university ? (
                  <div className="text-sm text-gray-600">{student.school || student.university}</div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Booking Details */}
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <span>📅</span>
                <span>{isArabic ? 'التاريخ' : 'Date'}</span>
              </div>
              <div className="font-bold text-gray-900">
                {formatDate(request.date)}
              </div>
            </div>

            <div className="p-4 bg-purple-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <span>🕐</span>
                <span>{isArabic ? 'الوقت' : 'Time'}</span>
              </div>
              <div className="font-bold text-gray-900">
                {request.time_from} - {request.time_to}
                {duration && <span className="text-sm font-normal text-gray-600 ml-2">({duration})</span>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <span>📚</span>
                <span>{isArabic ? 'المادة' : 'Subject'}</span>
              </div>
              <div className="font-bold text-gray-900">{request.subject || (isArabic ? 'غير محدد' : 'Not specified')}</div>
            </div>

            <div className="p-4 bg-orange-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <span>{request.request_type === 'group' ? '👥' : '👤'}</span>
                <span>{isArabic ? 'نوع الجلسة' : 'Session Type'}</span>
              </div>
              <div className="font-bold text-gray-900 capitalize">
                {request.request_type === 'group' ? (isArabic ? 'جماعية' : 'Group') : (isArabic ? 'فردية' : 'Individual')}
                {request.num_students && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    ({request.num_students} {isArabic ? 'طلاب' : 'students'})
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Session Format & Location */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
              {request.lesson_format === 'online' || request.place === 'online' ? (
                <>
                  <span>🌐</span>
                  <span>{isArabic ? 'نوع الجلسة' : 'Session Type'}</span>
                </>
              ) : (
                <>
                  <span>📍</span>
                  <span>{isArabic ? 'الموقع' : 'Location'}</span>
                </>
              )}
            </div>
            <div className="font-bold text-gray-900">
              {request.lesson_format === 'online' || request.place === 'online' ? (
                <span style={{ color: '#2563eb' }}>{isArabic ? 'جلسة عبر الإنترنت' : 'Online Session'}</span>
              ) : request.location_latitude && request.location_longitude ? (
                <a
                  href={createMapsUrl(request.location_latitude, request.location_longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                >
                  {request.location_formatted_address || request.place || (isArabic ? 'عرض الموقع' : 'View Location')}
                  <span style={{ fontSize: '12px' }}>↗</span>
                </a>
              ) : (
                <span>{request.place || (isArabic ? 'سيتم الترتيب' : 'To be arranged')}</span>
              )}
            </div>
          </div>

          {request.total_price && (
            <div className="p-4 bg-yellow-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <span>💰</span>
                <span>{isArabic ? 'السعر الكلي' : 'Total Price'}</span>
              </div>
              <div className="font-bold text-2xl text-gray-900">{request.total_price} BD</div>
            </div>
          )}

          {request.meeting_link && request.payment_status === 'paid' && (request.lesson_format === 'online' || request.place === 'online') && (
            <div className="p-4 bg-indigo-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                <span>🔗</span>
                <span>{isArabic ? 'رابط الاجتماع' : 'Meeting Link'}</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={request.meeting_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-semibold text-sm break-all flex-1"
                >
                  {request.meeting_link}
                </a>
                <button
                  onClick={copyMeetingLink}
                  className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition whitespace-nowrap"
                >
                  {isArabic ? 'نسخ' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {request.status === 'pending' && onAction && (
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                onAction(request.id, 'accepted');
                onClose();
              }}
              className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition"
            >
              ✅ {isArabic ? 'اقبل الطلب' : 'Accept Request'}
            </button>
            <button
              onClick={() => {
                onAction(request.id, 'declined');
                onClose();
              }}
              className="flex-1 bg-red-600 text-white py-3 rounded-xl font-bold hover:bg-red-700 transition"
            >
              ❌ {isArabic ? 'رفض الطلب' : 'Reject Request'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const StudentRequestsTable = React.memo(function StudentRequestsTable({
  requests = [],
  onAccept,
  onReject,
  isArabic,
  onViewStudent,
  studentProfiles = {}, // NEW: Student profiles passed from parent
  limit = 5,
  actionLoading = null // Loading state for accept/reject actions
}) {
  const [detailsModal, setDetailsModal] = useState({ open: false, request: null, student: null });

  const translations = {
    title: { en: 'Student Requests', ar: 'طلبات الطلاب' },
    subtitle: { en: 'PENDING APPROVAL', ar: 'بانتظار الموافقة' },
    viewAll: { en: 'View all requests →', ar: 'عرض جميع الطلبات ←' },
    columns: {
      student: { en: 'STUDENT', ar: 'الطالب' },
      subject: { en: 'SUBJECT INTEREST', ar: 'المادة المطلوبة' },
      time: { en: 'REQUESTED TIME', ar: 'الوقت المطلوب' },
      format: { en: 'FORMAT', ar: 'النوع' }, // NEW column
      action: { en: 'ACTION', ar: 'الإجراء' }
    },
    newStudent: { en: 'New Student', ar: 'طالب جديد' },
    noRequests: { en: 'No pending requests', ar: 'لا توجد طلبات معلقة' },
    online: { en: 'Online', ar: 'عبر الإنترنت' },
    faceToFace: { en: 'Face-to-Face', ar: 'وجهاً لوجه' }
  };

  const displayedRequests = requests.slice(0, limit);

  // Format time range
  const formatTimeRange = (date, timeFrom, timeTo) => {
    const formatTime = (time) => {
      if (!time) return '';
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? (isArabic ? 'م' : 'PM') : (isArabic ? 'ص' : 'AM');
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    const formattedDate = new Date(date).toLocaleDateString(
      isArabic ? 'ar-BH' : 'en-US',
      { month: 'short', day: 'numeric' }
    );

    return `${formattedDate}, ${formatTime(timeFrom)}`;
  };

  // Check if student is new (you can enhance this logic based on your data)
  const isNewStudent = (request) => {
    // This is a placeholder - you might want to check if this is their first request
    return request.is_new_student || false;
  };

  // Get student profile for a request
  const getStudentProfile = (request) => {
    return studentProfiles[request.student_email] || {
      full_name: request.student_name,
      profile_photo: null,
      email: request.student_email
    };
  };

  // Handle row click to show details
  const handleRowClick = (request) => {
    const student = getStudentProfile(request);
    setDetailsModal({ open: true, request, student });
  };

  // Handle action from modal
  const handleAction = (requestId, status) => {
    if (status === 'accepted' && onAccept) {
      onAccept(requestId);
    } else if (status === 'declined' && onReject) {
      onReject(requestId);
    }
  };

  return (
    <>
      <div
        className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100"
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="mb-4">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">
            {translations.title[isArabic ? 'ar' : 'en']}
          </h3>
          <p className="text-xs text-gray-500 font-medium">
            {translations.subtitle[isArabic ? 'ar' : 'en']}
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className={`pb-3 text-left text-xs font-semibold text-gray-500 uppercase ${isArabic ? 'text-right' : 'text-left'}`}>
                  {translations.columns.student[isArabic ? 'ar' : 'en']}
                </th>
                <th className={`pb-3 text-left text-xs font-semibold text-gray-500 uppercase ${isArabic ? 'text-right' : 'text-left'}`}>
                  {translations.columns.subject[isArabic ? 'ar' : 'en']}
                </th>
                <th className={`pb-3 text-left text-xs font-semibold text-gray-500 uppercase ${isArabic ? 'text-right' : 'text-left'}`}>
                  {translations.columns.format[isArabic ? 'ar' : 'en']}
                </th>
                <th className={`pb-3 text-left text-xs font-semibold text-gray-500 uppercase ${isArabic ? 'text-right' : 'text-left'}`}>
                  {translations.columns.time[isArabic ? 'ar' : 'en']}
                </th>
                <th className={`pb-3 text-left text-xs font-semibold text-gray-500 uppercase ${isArabic ? 'text-right' : 'text-left'}`}>
                  {translations.columns.action[isArabic ? 'ar' : 'en']}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedRequests.length > 0 ? (
                displayedRequests.map((request) => {
                  const student = getStudentProfile(request);
                  return (
                    <tr
                      key={request.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(request)}
                    >
                      {/* Student */}
                      <td className="py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={student.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
                            alt={student.full_name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                          />
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">
                              {student.full_name}
                            </p>
                            {isNewStudent(request) && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                {translations.newStudent[isArabic ? 'ar' : 'en']}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Subject */}
                      <td className="py-4">
                        <span className="text-sm text-blue-600 font-medium">
                          {request.subject || request.request_type}
                        </span>
                      </td>

                      {/* Format - NEW COLUMN */}
                      <td className="py-4">
                        {request.lesson_format === 'online' || request.place === 'online' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            <span>🌐</span>
                            <span>{translations.online[isArabic ? 'ar' : 'en']}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <span>📍</span>
                            <span>{translations.faceToFace[isArabic ? 'ar' : 'en']}</span>
                          </span>
                        )}
                      </td>

                      {/* Time */}
                      <td className="py-4">
                        <span className="text-sm text-gray-700">
                          {formatTimeRange(request.date, request.time_from, request.time_to)}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onReject && onReject(request.id)}
                            disabled={actionLoading === request.id + '-rejected'}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                              actionLoading === request.id + '-rejected'
                                ? 'opacity-60 cursor-not-allowed'
                                : 'hover:bg-gray-100'
                            }`}
                            title={isArabic ? 'رفض' : 'Reject'}
                          >
                            {actionLoading === request.id + '-rejected' ? (
                              <span className="animate-spin text-sm">⏳</span>
                            ) : (
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => onAccept && onAccept(request.id)}
                            disabled={actionLoading === request.id + '-accepted'}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                              actionLoading === request.id + '-accepted'
                                ? 'opacity-60 cursor-not-allowed bg-green-100'
                                : 'hover:bg-green-50'
                            }`}
                            title={isArabic ? 'قبول' : 'Accept'}
                          >
                            {actionLoading === request.id + '-accepted' ? (
                              <span className="animate-spin text-sm">⏳</span>
                            ) : (
                              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-400 text-sm">
                    {translations.noRequests[isArabic ? 'ar' : 'en']}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {displayedRequests.length > 0 ? (
            displayedRequests.map((request) => {
              const student = getStudentProfile(request);
              return (
                <div
                  key={request.id}
                  className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleRowClick(request)}
                >
                  {/* Student Info */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={student.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
                        alt={student.full_name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                      />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">
                          {student.full_name}
                        </p>
                        {isNewStudent(request) && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                            {translations.newStudent[isArabic ? 'ar' : 'en']}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Subject, Format & Time */}
                  <div className="mb-3 space-y-2">
                    <div>
                      <span className="text-xs text-gray-500 uppercase">
                        {translations.columns.subject[isArabic ? 'ar' : 'en']}
                      </span>
                      <p className="text-sm text-blue-600 font-medium">
                        {request.subject || request.request_type}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase">
                        {translations.columns.format[isArabic ? 'ar' : 'en']}
                      </span>
                      <div className="mt-1">
                        {request.lesson_format === 'online' || request.place === 'online' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            <span>🌐</span>
                            <span>{translations.online[isArabic ? 'ar' : 'en']}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <span>📍</span>
                            <span>{translations.faceToFace[isArabic ? 'ar' : 'en']}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase">
                        {translations.columns.time[isArabic ? 'ar' : 'en']}
                      </span>
                      <p className="text-sm text-gray-700">
                        {formatTimeRange(request.date, request.time_from, request.time_to)}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onReject && onReject(request.id)}
                      disabled={actionLoading === request.id + '-rejected'}
                      className={`flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
                        actionLoading === request.id + '-rejected'
                          ? 'opacity-60 cursor-not-allowed bg-gray-100'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {actionLoading === request.id + '-rejected' ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="animate-spin">⏳</span>
                          {isArabic ? 'جارٍ...' : 'Loading...'}
                        </span>
                      ) : (
                        isArabic ? 'رفض' : 'Reject'
                      )}
                    </button>
                    <button
                      onClick={() => onAccept && onAccept(request.id)}
                      disabled={actionLoading === request.id + '-accepted'}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium text-white transition-colors ${
                        actionLoading === request.id + '-accepted'
                          ? 'opacity-60 cursor-not-allowed bg-green-400'
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {actionLoading === request.id + '-accepted' ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="animate-spin">⏳</span>
                          {isArabic ? 'جارٍ القبول...' : 'Accepting...'}
                        </span>
                      ) : (
                        isArabic ? 'قبول' : 'Accept'
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="py-8 text-center text-gray-400 text-sm">
              {translations.noRequests[isArabic ? 'ar' : 'en']}
            </div>
          )}
        </div>

        {/* View All Link */}
        {requests.length > limit && (
          <div className="mt-4 text-center">
            <button
              onClick={() => {/* Navigate to requests page */}}
              className="text-brand-orange text-sm font-medium hover:underline"
            >
              {translations.viewAll[isArabic ? 'ar' : 'en']}
            </button>
          </div>
        )}
      </div>

      {/* Request Details Modal */}
      <RequestDetailsModal
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, request: null, student: null })}
        request={detailsModal.request}
        student={detailsModal.student}
        isArabic={isArabic}
        onAction={handleAction}
      />
    </>
  );
});

export default StudentRequestsTable;
